<template>
  <section class="redirects settings-grid">
    <slot name="menu"></slot>

    <div
      ref="container"
      class="settings-grid__main custom-scrollbar"
    >
      <div class="settings-grid__main__header">
        <slot name="title"></slot>

        <div class="no-select search">
          <IconSearch size="sm" />
          <input
            id="modelPath"
            ref="search__field"
            v-model="searchTerm"
            class="form-control search__field"
            type="text"
            placeholder="Buscar redirecionamento"
            autocomplete="off"
          />
        </div>

        <button
          class="btn btn-secondary btn-icon"
          :disabled="isViewerUser"
          @click="createRedirect()"
        >
          <IconPlus size="xs" />
          <span> Criar novo redirecionamento </span>
        </button>
      </div>

      <Loader
        v-if="loading"
        color="#3E9AA6"
      />
      <div
        v-else-if="redirects.length > 0"
        class="redirects__list custom-scrollbar"
      >
        <div class="table-head table-row">
          <div class="table-cell">URL antiga</div>
          <div class="table-cell">URL nova</div>
          <div class="table-cell">Redirecionamento</div>
          <div class="table-cell">Ações</div>
        </div>
        <div class="table-body">
          <div
            v-for="rule in filteredRedirects"
            :key="rule.id"
            class="table-row"
          >
            <div class="redirect-rule__from table-cell">
              <span
                class="ellipsis-single-line"
                :title="rule.from"
              >
                {{ rule.from }}
              </span>
            </div>
            <div class="redirect-rule__to table-cell">
              <span
                class="ellipsis-single-line"
                :title="rule.to"
              >
                {{ rule.to }}
              </span>
            </div>
            <div class="redirect-rule__redirect table-cell">
              <span
                v-if="rule.method === 'group'"
                class="group-tag"
                title="Redirecionamento por grupo de URLs"
              >
                Grupo
              </span>
              <span>{{ redirectLabels[rule.statusCode] }}</span>
            </div>
            <div class="redirect-rule__actions table-cell">
              <button @click.stop="toggleActions(rule.id)">
                <IconDotsVert />
              </button>
              <ul
                v-show="openedActions === rule.id"
                ref="dropdown"
                v-clickaway="closeActions"
                class="redirect-rule__actions-dropdown"
              >
                <li>
                  <button @click.stop="showEditModal(rule)">Editar</button>
                </li>
                <li>
                  <button
                    class="text-danger"
                    :disabled="isViewerUser"
                    @click.stop="showDeleteModal(rule)"
                  >
                    Deletar
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="settings-grid__empty-state"
      >
        Você ainda não criou nenhum redirecionamento 😿
      </div>
    </div>

    <div
      v-show="creatingRedirect || redirectBeingEdited"
      class="edit-modal"
      @click.self="closeModal"
    >
      <div class="edit-modal__content">
        <div
          class="btn-close"
          title="Fechar modal"
          @click="closeModal"
        >
          <IconClose />
        </div>

        <div class="edit-modal__header mb-4">
          <h2>{{ currentAction }} redirecionamento</h2>
        </div>

        <form
          class="edit-modal__form"
          @submit.prevent="saveRedirect"
        >
          <div class="form-section mb-3">
            <p class="form-section__title">
              Selecione o tipo de redirecionamento:
            </p>
            <div class="redirect-type-wrapper">
              <div class="radio-field">
                <div class="radio-wrapper">
                  <label class="radio-container">
                    <input
                      id="permanent-redirect"
                      v-model="redirectType"
                      type="radio"
                      spellcheck="false"
                      name="redirect-type"
                      :disabled="isViewerUser"
                      :value="301"
                    />
                    <IconRadio
                      class="text-primary icon"
                      :checked="redirectType === 301"
                    />
                    Permanente
                  </label>
                  <div class="icon-wrapper">
                    <span class="slb-tooltip">
                      Redirecionamento permanente (301) ficará sempre ativo, até
                      ser deletado. Pode ficar em cache no navegador.
                    </span>
                    <IconHelp
                      size="xs"
                      color="#474747"
                    />
                  </div>
                </div>
                <div class="radio-wrapper">
                  <label class="radio-container">
                    <input
                      id="temporary-redirect"
                      v-model="redirectType"
                      type="radio"
                      spellcheck="false"
                      name="redirect-type"
                      :disabled="isViewerUser"
                      :value="302"
                    />
                    <IconRadio
                      class="text-primary icon"
                      :checked="redirectType === 302"
                    />
                    Temporário
                  </label>
                  <div class="icon-wrapper">
                    <span class="slb-tooltip">
                      Redirecionamentos temporários (302) ficarão ativos apenas
                      por um período de tempo indeterminado.
                    </span>
                    <IconHelp
                      size="xs"
                      color="#474747"
                    />
                  </div>
                </div>
                <p
                  v-show="errors['redirect-type']"
                  class="invalid-feedback"
                >
                  {{ errors['redirect-type'] }}
                </p>
              </div>
              <div
                v-if="redirectType === 302"
                class="d-flex datepickers"
              >
                <DatetimePicker
                  label="Início do período de redirect"
                  :value="redirectStart"
                  :disabled="isViewerUser"
                  @change="redirectStart = $event"
                />
                <DatetimePicker
                  label="Fim do período de redirect"
                  :value="redirectEnd"
                  :disabled="isViewerUser"
                  @change="redirectEnd = $event"
                />
              </div>
            </div>
          </div>

          <div class="form-section mb-4">
            <p class="form-section__title">
              Defina as URLs e a forma que deseja redirecionar:
            </p>

            <div class="radio-field mb-3">
              <div class="radio-wrapper">
                <label class="radio-container">
                  <input
                    id="single-redirect"
                    v-model="redirectMethod"
                    type="radio"
                    spellcheck="false"
                    name="redirect-method"
                    :disabled="isViewerUser"
                    value="single"
                  />
                  <IconRadio
                    class="text-primary icon"
                    :checked="redirectMethod === 'single'"
                  />
                  Redirecionamento único
                </label>
                <div class="icon-wrapper">
                  <span class="slb-tooltip">
                    Redirecionamento de uma URL específica para outra.
                  </span>
                  <IconHelp
                    size="xs"
                    color="#474747"
                  />
                </div>
              </div>

              <div class="radio-wrapper">
                <label class="radio-container">
                  <input
                    id="group-redirect"
                    v-model="redirectMethod"
                    type="radio"
                    spellcheck="false"
                    name="redirect-method"
                    :disabled="isViewerUser"
                    value="group"
                  />
                  <IconRadio
                    class="text-primary icon"
                    :checked="redirectMethod === 'group'"
                  />
                  Redirecionamento de grupo de URL
                </label>
                <div class="icon-wrapper">
                  <span class="slb-tooltip">
                    Redirecionamentos com base no início da URL. O trecho após a
                    prefixo informado é mantido.
                  </span>
                  <IconHelp
                    size="xs"
                    color="#474747"
                  />
                </div>
              </div>
            </div>

            <div class="row">
              <div class="edit-modal__form-url col">
                <label for="url-from">Digite a URL antiga</label>
                <div
                  class="url-wrapper"
                  :class="{ 'group-redirect': redirectMethod === 'group' }"
                >
                  <input
                    id="url-from"
                    v-model.trim="urlFrom"
                    type="text"
                    autocomplete="off"
                    spellcheck="false"
                    placeholder="https://exemplo.com.br/url-antiga"
                    :class="{ 'is-invalid': errors['url-from'] }"
                    :disabled="isViewerUser"
                    @input="validateUrlFrom"
                  />
                  <span class="url-tag">resto da URL</span>
                </div>
                <p
                  v-show="errors['url-from']"
                  class="invalid-feedback"
                >
                  {{ errors['url-from'] }}
                </p>
              </div>
              <div class="middle-arrow col">
                <div
                  class="middle-arrow__box"
                  title="Redireciona para..."
                >
                  <IconArrow direction="right" />
                </div>
              </div>
              <div class="edit-modal__form-url col">
                <label for="url-to">Digite a nova URL</label>
                <div
                  class="url-wrapper"
                  :class="{ 'group-redirect': redirectMethod === 'group' }"
                >
                  <input
                    id="url-to"
                    v-model.trim="urlTo"
                    type="text"
                    autocomplete="off"
                    spellcheck="false"
                    placeholder="https://exemplo.com.br/url-nova"
                    :class="{ 'is-invalid': errors['url-to'] }"
                    :disabled="isViewerUser"
                    @input="validateUrlTo"
                  />

                  <span class="url-tag">resto da URL</span>
                </div>
                <p
                  v-show="errors['url-to']"
                  class="invalid-feedback"
                >
                  {{ errors['url-to'] }}
                </p>
              </div>
            </div>
          </div>

          <div class="edit-modal__buttons">
            <button
              class="btn btn-secondary btn-white"
              type="button"
              @click="closeModal"
            >
              Cancelar
            </button>
            <button
              class="btn btn-secondary"
              :disabled="isViewerUser"
              type="submit"
            >
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div
      v-show="redirectBeingDeleted"
      class="remove-modal"
      @click.self="closeModal"
    >
      <div class="remove-modal__content">
        <div
          class="btn-close"
          title="Fechar modal"
          @click="closeModal"
        >
          <IconClose />
        </div>

        <div class="remove-modal__header">
          <h2>Tem certeza que deseja deletar o redirecionamento?</h2>
        </div>

        <div
          v-if="redirectBeingDeleted && redirectBeingDeleted.from"
          class="remove-modal__text"
        >
          <p>
            Ao deletar o redirecionamento, a URL passará a direcionar novamente
            para a sua página original.
          </p>
        </div>

        <div class="remove-modal__buttons">
          <button
            class="btn btn-neutral"
            type="button"
            @click="closeModal"
          >
            Voltar
          </button>
          <button
            class="btn btn-danger"
            type="submit"
            @click="deleteRedirect"
          >
            {{ saving ? 'Deletando...' : 'Deletar redirecionamento' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Vue from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import debounce from 'lodash/debounce';
import slugify from 'slugify';

import DatetimePicker from '@/ui-types/DatetimePicker.vue';
import Loader from '@/components/Loader.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconHelp from '@/components/icons/IconHelp.vue';
import IconPlus from '@/components/icons/IconPlus.vue';
import IconRadio from '@/components/icons/IconRadio.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import IconDotsVert from '@/components/icons/IconDotsVert.vue';
import IconSearch from '@/components/icons/IconSearch.vue';

export default {
  name: 'Redirects',
  components: {
    DatetimePicker,
    IconClose,
    IconHelp,
    IconPlus,
    IconRadio,
    IconArrow,
    IconDotsVert,
    IconSearch,
    Loader,
  },
  directives: {
    // clickaway taken from https://stackoverflow.com/questions/36170425/detect-click-outside-element
    clickaway: {
      bind(el, binding, vnode) {
        // eslint-disable-next-line no-param-reassign
        el.clickOutsideEvent = (event) => {
          // here I check that click was outside the el and his children
          if (!(el === event.target || el.contains(event.target))) {
            // and if it did, call method provided in attribute value
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      },
    },
  },
  data() {
    return {
      openedActions: null,
      redirectBeingEdited: null,
      redirectBeingDeleted: null,
      creatingRedirect: false, // TODO: refact
      urlFrom: '',
      urlTo: '',
      redirectType: 301,
      redirectMethod: 'single',
      redirectStart: null,
      redirectEnd: null,
      redirectLabels: {
        301: 'Permanente',
        302: 'Temporário',
      },
      searchTerm: '',
      errors: {},
      saving: false,
    };
  },
  computed: {
    currentAction() {
      return this.redirectBeingEdited === null ? 'Criar' : 'Editar';
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
    filteredRedirects() {
      return this.searchTerm
        ? this.redirects.filter((redirect) => this.isRedirectMatch(redirect))
        : this.redirects;
    },
    ...mapState(['loading', 'redirects', 'customer', 'customerSettings']),
    ...mapGetters(['siteHostname', 'isViewerUser']),
  },
  mounted() {
    this.setDropdownsPosition();
    this.$refs.container.addEventListener(
      'scroll',
      debounce(this.setDropdownsPosition, 150),
    );
  },
  methods: {
    closeActions() {
      this.openedActions = null;
    },
    toggleActions(id) {
      if (this.openedActions === id) {
        this.openedActions = null;
      } else {
        this.openedActions = id;
      }
    },
    setDropdownsPosition() {
      const dropdowns = this.$refs.dropdown;

      dropdowns.forEach((dropdown) => {
        const position = dropdown.closest('.table-row').getBoundingClientRect();

        const dropdownHeight = 28 + 35 * dropdown.childElementCount;
        const elementPosition = position.bottom + dropdownHeight;
        const viewportEdge =
          (window.innerHeight || document.documentElement.clientHeight) - 120;

        if (elementPosition > viewportEdge) {
          // TODO FIX ME
          dropdown.setAttribute('drop-up', true);
        } else {
          dropdown.removeAttribute('drop-up');
        }
      });
    },
    showEditModal(redirect) {
      this.closeActions();
      this.redirectBeingEdited = redirect;
      this.urlFrom = redirect.from;
      this.urlTo = redirect.to;
      this.redirectType = redirect.statusCode;
      this.redirectMethod = redirect.method || 'single';
      this.redirectStart = redirect.redirectStart;
      this.redirectEnd = redirect.redirectEnd;
    },
    showDeleteModal(redirect) {
      this.closeActions();
      this.redirectBeingDeleted = redirect;
    },
    createRedirect() {
      this.creatingRedirect = true;
    },
    async saveRedirect() {
      this.validateUrlFrom();
      this.validateUrlTo();
      this.validateRedirect();

      if (!this.hasErrors) {
        this.saving = true;

        const rule = {
          from: this.urlFrom,
          to: this.urlTo,
          statusCode: this.redirectType,
          method: this.redirectMethod,
          redirectStart: this.redirectStart,
          redirectEnd: this.redirectEnd,
        };
        let action;

        if (this.redirectBeingEdited) {
          action = 'updateRedirect';
          rule.id = this.redirectBeingEdited.id;
        } else {
          action = 'addRedirect';
        }

        await this[action](rule);

        this.saving = false;
        this.closeModal();
      }
    },
    closeModal() {
      this.creatingRedirect = false;
      this.redirectBeingEdited = null;
      this.redirectBeingDeleted = null;
      this.urlFrom = '';
      this.urlTo = '';
      this.redirectType = 301;
      this.redirectMethod = 'single';
      this.redirectStart = null;
      this.redirectEnd = null;
      this.errors = {};
    },
    isRepeatedRule(urlFrom) {
      return this.redirects.some(
        (rule) =>
          rule.from === urlFrom && rule.id !== this.redirectBeingEdited?.id,
      );
    },
    validateUrlFrom() {
      // TODO: unify this method and validateUrlTo
      const fieldId = 'url-from';
      const urlRegex = /^((https:\/\/([a-z0-9\-.]+)(\/[^\s]*)?)|(\/[^\s]*))$/;

      if (!/\S/.test(this.urlFrom)) {
        Vue.set(this.errors, fieldId, 'Por favor, preencha a URL antiga');
      } else if (this.isRepeatedRule(this.urlFrom)) {
        Vue.set(
          this.errors,
          fieldId,
          'Ops! Já existe um redirecionamento para esta URL',
        );
      } else {
        // Matches full URLs (start with https) or relative URLs (start with "/").
        // [3] is the domain (if full URL)
        // [4] is the remainder after domain (if full URL)
        // [5] is the relative URL (if it starts with /)
        const match = this.urlFrom.match(urlRegex);

        if (!match) {
          Vue.set(
            this.errors,
            fieldId,
            'Ops! O endereço digitado não é válido',
          );
        } else if (
          match[3] !== undefined &&
          !this.customerSettings.domain.includes(match[3])
        ) {
          Vue.set(
            this.errors,
            fieldId,
            'Ops! O domínio da URL não está cadastrado',
          );
        } else {
          Vue.delete(this.errors, fieldId);
        }
      }
    },
    validateUrlTo() {
      const fieldId = 'url-to';
      // Matches full URLs (start with https) or relative URLs (start with "/").
      const urlRegex = /^((https:\/\/([a-z0-9\-.]+)(\/[^\s]*)?)|(\/[^\s]*))$/;

      if (!/\S/.test(this.urlTo)) {
        Vue.set(this.errors, fieldId, 'Por favor, preencha a URL nova');
      } else if (!urlRegex.test(this.urlTo)) {
        Vue.set(this.errors, fieldId, 'Ops! O endereço digitado não é válido');
      } else if (this.isSameUrl(this.urlTo, this.urlFrom)) {
        Vue.set(this.errors, fieldId, 'Ops! As duas URLs são iguais');
      } else {
        Vue.delete(this.errors, fieldId);
      }
    },
    validateRedirect() {
      const fieldId = 'redirect-type';

      if (!this.redirectType) {
        Vue.set(
          this.errors,
          fieldId,
          'Por favor, escolha um tipo de redirecionamento.',
        );
      } else {
        Vue.delete(this.errors, fieldId);
      }
    },
    isSameUrl(url1, url2) {
      const isUrl1Relative = /^\//.test(url1);
      const isUrl2Relative = /^\//.test(url2);

      if (isUrl1Relative === isUrl2Relative) {
        // both are relative or both are full URLs
        return url1 === url2;
      }

      try {
        const url1Complete = new URL(url1, `https://${this.siteHostname}`);
        const url2Complete = new URL(url2, `https://${this.siteHostname}`);

        return url1Complete.href === url2Complete.href;
      } catch (err) {
        return false;
      }
    },
    async deleteRedirect() {
      this.saving = true;

      await this.removeRedirect({ id: this.redirectBeingDeleted.id });

      this.saving = false;
      this.closeModal();
    },
    isRedirectMatch(redirect) {
      const term = slugify(this.searchTerm, { lower: true, strict: true });
      const { from, to, statusCode, method } = redirect;
      const methodLabels = {
        single: 'Único',
        group: 'Grupo',
      };

      const string = [
        from,
        to,
        this.redirectLabels[statusCode],
        methodLabels[method],
      ].join(' ');

      const slug = slugify(string, {
        lower: true,
        strict: true,
      });

      return slug.includes(term);
    },
    ...mapActions(['addRedirect', 'removeRedirect', 'updateRedirect']),
  },
};
</script>

<style lang="scss" scoped>
.redirects {
  .settings-grid {
    &__main__header {
      &::after {
        display: none; // hides the fade
      }

      .search {
        position: relative;
        width: 100%;
        max-width: 300px;

        i {
          position: absolute;
          right: 0;
          top: 0;
          margin: 6px 0;
        }

        &__field {
          background: none;
          padding-right: 25px;

          &::placeholder {
            color: #949494;
          }
        }
      }
    }

    &__empty-state {
      margin-top: 50px;
      font-size: $size-normal;
      text-align: center;
    }
  }

  &__list {
    width: 100%;
    height: 100%;

    button {
      outline: none;
      border: none;
      background: none;
    }

    .table-head {
      position: sticky !important;
      top: 0;
      z-index: 1;
      font-weight: $lb-medium;
      background: $lb-white;
    }

    .table-row {
      padding: 2px 12px;
      position: relative;
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      border-bottom: 1px solid rgba($lb-light-gray, 0.5);

      > * {
        width: 100%; // defaults to equal widths

        &:nth-child(3) {
          // tipo de redirect
          width: 150px;
          justify-content: flex-end;
          flex-shrink: 0;
        }
        &:last-child {
          // ações
          width: 70px;
          justify-content: center;
          flex-shrink: 0;
        }
      }
    }

    .table-cell {
      display: flex;
      align-items: center;
      padding: 8px 4px;
      overflow: hidden;
    }

    .group-tag {
      font-size: $size-smaller;
      color: $gray-800;
      background: #e8e8e8;
      border-radius: 100px;
      margin-right: 8px;
      padding: 2px 10px;
    }

    .redirect-rule__actions {
      padding: 0;

      > button {
        color: #3e9aa6;
        padding: 4px;
        border-radius: 50%;
        outline: none;

        &:hover {
          background: #d1dafb;
        }
      }

      &-dropdown {
        background-color: $lb-white;
        box-shadow: 4px 4px 16px rgba($lb-black, 0.25);
        position: absolute;
        top: 100%;
        right: 16px;
        z-index: 1;
        padding: 1em 0;

        &[drop-up] {
          top: auto;
          bottom: 100%;
        }

        button {
          padding: 0.5em 1.5em;
          cursor: pointer;
          width: 100%;
          text-align: left;

          &[disabled] {
            opacity: 0.5;
          }

          &:hover {
            background-color: $lb-ice;
          }
        }
      }
    }
  }

  &::v-deep {
    .instance-properties.instance-properties--full {
      height: 100vh;
    }
  }

  .edit-modal {
    position: fixed;
    top: 45px;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99999;

    &::v-deep .field {
      margin: 0;
    }

    &__content {
      width: 900px;
      display: flex;
      padding: 40px;
      flex-direction: column;
      background-color: $lb-white;
      position: relative;
    }

    &__header {
      width: 100%;

      h2 {
        font-size: $size-bigger;
      }

      p {
        color: #474747;
      }
    }

    &__form {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      .invalid-feedback {
        display: block;
        position: absolute;
        bottom: 0;
      }

      label {
        margin-bottom: 0;
      }

      input {
        border: none;
        border-bottom: 1px solid $lb-dark-gray;
        outline: none;
        padding-bottom: 6px;

        &:invalid,
        &.is-invalid {
          border-bottom-color: #e0414b;
          box-shadow: none;
        }

        &::placeholder {
          color: #c2c2c2;
        }
      }

      .form-section {
        &__title {
          font-size: $size-normal;
          font-weight: $lb-medium;
          color: #474747;
          margin-bottom: 1rem;
        }
      }

      .redirect-type-wrapper {
        display: flex;

        .radio-field {
          flex-shrink: 0;
          margin-right: 28px;
        }

        .datepickers .field + .field {
          margin-left: 8px;
        }
      }

      &-url {
        display: flex;
        flex-direction: column;
        position: relative;
        padding-bottom: 24px; // space for error message
        color: #474747;

        label {
          font-weight: $lb-medium;
          margin-bottom: 8px;
          font-size: $size-small;
        }

        input {
          width: 100%;
        }

        .field {
          margin: 0;
        }

        .url-wrapper {
          position: relative;

          &.group-redirect {
            input {
              padding-right: 90px;
            }

            .url-tag {
              display: inline-block;
            }
          }
        }

        .url-tag {
          display: none;
          font-size: $size-smaller;
          line-height: 13px;
          color: $gray-500;
          background: #e8e8e8;
          border-radius: 100px;
          padding: 4px 10px;
          position: absolute;
          right: 0;
        }
      }

      .radio-field {
        display: flex;
        position: relative;
        padding-bottom: 24px; // space for error message

        .radio-wrapper {
          display: flex;
          align-items: center;

          .radio-container {
            display: flex;
            align-items: center;
            cursor: pointer;

            input {
              position: absolute;
              opacity: 0;
              height: 0;
              width: 0;
            }

            .icon {
              margin-right: 8px;
            }
          }

          & + .radio-wrapper {
            margin-left: 50px;
          }

          .icon-wrapper {
            margin-left: 12px;
          }
        }
      }
    }

    .middle-arrow {
      display: flex;
      justify-content: center;
      align-items: center;
      max-width: 88px;

      &__box {
        padding: 10px;
        background: $gray-10;
        border-radius: 4px;
      }
    }

    &__buttons {
      display: flex;
      justify-content: space-between;

      button {
        min-width: 140px;
      }
    }

    &__sub-title {
      font-size: $size-smaller;
      font-weight: $lb-normal;
      color: #7f7f7f;
    }
  }

  .remove-modal {
    position: fixed;
    top: 45px;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;

    &__content {
      width: 520px;
      display: flex;
      padding: 40px 50px;
      flex-direction: column;
      background-color: $lb-white;
      position: relative;
    }

    &__header {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      flex-direction: column;
      position: relative;

      h2 {
        font-size: $size-bigger;
        font-weight: $lb-medium;
        line-height: 35px;
        text-align: center;
        letter-spacing: -1px;
        color: #474747;
        margin: 28px 0;
      }

      .btn-close {
        position: absolute;
        top: 0;
        right: 0;
        cursor: pointer;
      }
    }

    &__text {
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
      margin-bottom: 24px;

      p {
        font-size: $size-normal;
        line-height: 22px;
        color: #474747;
        margin-bottom: 16px;
      }
    }

    &__buttons {
      display: flex;
      justify-content: space-between;
      align-items: center;

      button {
        min-width: 140px;
      }
    }
  }

  .btn-close {
    position: absolute;
    right: 0;
    top: 0;
    padding: 12px;
    cursor: pointer;

    .icon {
      display: block;
      font-size: 22px;
    }
  }

  .icon-wrapper {
    display: flex;
    position: relative;

    .slb-tooltip {
      visibility: hidden;
      opacity: 0;
      margin-left: 0;
    }

    &:hover {
      .slb-tooltip {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }
    }
  }
}
</style>
